import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import type { TrackerImage } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { TrackerShotImage } from './TrackerShotImage';
import { formatDateTimeIST } from '@/lib/dateFormat';
import { formatFileSize } from '@/lib/fileFormat';

interface Props {
  shots: TrackerImage[];
  index: number | null;
  ownerName: (userId: string) => string;
  onIndexChange: (index: number | null) => void;
}

/** Full-size viewer over the current gallery page; ← / → step through it. */
export function TrackerLightbox({ shots, index, ownerName, onIndexChange }: Props) {
  const shot = index === null ? undefined : shots[index];
  const hasPrev = index !== null && index > 0;
  const hasNext = index !== null && index < shots.length - 1;

  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      if (e.key === 'ArrowRight' && index < shots.length - 1) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, shots.length, onIndexChange]);

  return (
    <Dialog open={!!shot} onOpenChange={(open) => { if (!open) onIndexChange(null); }}>
      <DialogContent className="max-w-5xl gap-3 bg-neutral-950 p-3 text-neutral-100 sm:p-4">
        {shot && (
          <>
            <div className="flex max-h-[70vh] items-center justify-center overflow-hidden rounded-lg bg-black">
              <TrackerShotImage key={shot.id} image={shot} className="max-h-[70vh] w-full !object-contain" />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 text-xs">
                <DialogTitle className="text-sm font-semibold text-neutral-100">{formatDateTimeIST(shot.capturedAt)}</DialogTitle>
                <DialogDescription className="text-neutral-400">
                  {shot.monitor} · {ownerName(shot.userId)}
                  {shot.width && shot.height ? ` · ${shot.width}×${shot.height}` : ''}
                  {shot.bytes ? ` · ${formatFileSize(shot.bytes)}` : ''}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="secondary" disabled={!hasPrev} onClick={() => index !== null && onIndexChange(index - 1)}>
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <Button size="sm" variant="secondary" disabled={!hasNext} onClick={() => index !== null && onIndexChange(index + 1)}>
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
                {shot.url && (
                  <Button size="sm" variant="secondary" asChild>
                    <a href={shot.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" /> Open
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
